import { memo, useMemo, useState } from "react";
import { ChevronDown, ExternalLink, ShieldCheck } from "lucide-react";
import type { Finding } from "@/lib/intel/mock";
import { LANE_LABEL, LANE_ORDER, type Lane } from "@/lib/intel/lanes";
import { SeverityBadge } from "@/components/intel/SeverityBadge";

/**
 * Analyst read of a scan: findings grouped into evidence lanes, each row
 * carrying its own provenance (source, link, retrieval time, corroboration).
 */
function EvidenceSectionsImpl({ findings }: { findings: Finding[] }) {
  const grouped = useMemo(() => {
    const m = new Map<Lane, Finding[]>();
    for (const f of findings) {
      const lane = (LANE_ORDER as string[]).includes(f.lane ?? "") ? (f.lane as Lane) : "other";
      m.set(lane, [...(m.get(lane) ?? []), f]);
    }
    for (const [, list] of m) list.sort((a, b) => b.confidence - a.confidence);
    return m;
  }, [findings]);

  if (!findings.length) {
    return (
      <div className="rounded-md border border-panel-border bg-background/40 p-6 text-center text-sm text-muted-foreground">
        No evidence yet. Run an investigation to populate this view.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {LANE_ORDER.map((lane) => {
        const list = grouped.get(lane);
        return <LaneCard key={lane} lane={lane} findings={list ?? []} />;
      })}
    </div>
  );
}

function LaneCard({ lane, findings }: { lane: Lane; findings: Finding[] }) {
  const [open, setOpen] = useState(findings.length > 0 && lane !== "other");
  const empty = findings.length === 0;
  return (
    <section className="glass overflow-hidden rounded-xl">
      <button
        type="button"
        onClick={() => !empty && setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-sm">{LANE_LABEL[lane]}</span>
        <span className="rounded-full bg-background/60 px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
          {findings.length}
        </span>
        {empty && <span className="text-xs text-muted-foreground">nothing found in this lane</span>}
        {!empty && <ChevronDown className={`ml-auto h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && !empty && (
        <div className="space-y-2 border-t border-panel-border px-4 py-3">
          {findings.slice(0, 60).map((f) => (
            <FindingRow key={f.id} f={f} />
          ))}
          {findings.length > 60 && (
            <p className="pt-1 text-xs text-muted-foreground">+{findings.length - 60} more in this lane.</p>
          )}
        </div>
      )}
    </section>
  );
}

function FindingRow({ f }: { f: Finding }) {
  return (
    <div className="rounded-md border border-panel-border bg-background/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <SeverityBadge s={f.severity} />
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{f.category}</span>
        {typeof f.corroboration === "number" && f.corroboration > 1 && (
          <span className="inline-flex items-center gap-1 font-mono text-[10px] text-success">
            <ShieldCheck className="h-3 w-3" /> {f.corroboration}x corroborated
          </span>
        )}
        <span className="ml-auto font-mono text-[10px] text-cyan">conf {f.confidence}%</span>
      </div>
      <div className="mt-1 break-words text-sm font-medium">{f.title}</div>
      <div className="mt-0.5 break-words text-sm text-muted-foreground">{f.description}</div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-muted-foreground">
        <span className="uppercase tracking-widest">src · {f.source}</span>
        <span>{f.ts}</span>
        {typeof f.latencyMs === "number" && <span>{f.latencyMs}ms</span>}
        {f.url && (
          <a
            href={f.url}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-cyan hover:underline"
          >
            open source <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

/** Memoised: these panels render large evidence sets and must not re-render on every keystroke. */
export const EvidenceSections = memo(EvidenceSectionsImpl);
